import type { SuggestionOptions } from "@tiptap/suggestion";
import type { Editor } from "@tiptap/react";
import { ReactRenderer, posToDOMRect } from "@tiptap/react";
import { computePosition, flip, shift } from "@floating-ui/dom";
import { CoreExtensionNames } from "../../constants/extention";
import { getExtensionStorage } from "../../helpers/get-extension-storage";
import type { MentionHandler } from "./types";
import type { MentionsListDropdownProps } from "./mentions-list-dropdown";
import { MentionsListDropdown } from "./mentions-list-dropdown";

export interface CommandListInstance {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to
      )
  };

  void computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()]
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export const renderMentionsDropdown =
  (
    renderProps: Pick<MentionHandler, "searchCallback">
  ): SuggestionOptions["render"] =>
  // @ts-expect-error - Tiptap types are incorrect
  () => {
    const { searchCallback } = renderProps;
    let component: ReactRenderer<
      CommandListInstance,
      MentionsListDropdownProps
    > | null = null;

    return {
      onStart: (props: { editor: Editor; clientRect?: DOMRect }) => {
        if (!searchCallback) return;
        if (!props.clientRect) return;

        component = new ReactRenderer<
          CommandListInstance,
          MentionsListDropdownProps
        >(MentionsListDropdown, {
          props: {
            ...props,
            searchCallback
          },
          editor: props.editor
        });

        // Track active dropbar extension
        getExtensionStorage(
          props.editor,
          CoreExtensionNames.UTILITY
        ).activeDropbarExtensions.push(CoreExtensionNames.MENTION);

        // Ensure component.element is an HTMLElement before using HTMLElement properties
        if (component.element instanceof HTMLElement) {
          component.element.style.position = "absolute";
          document.body.appendChild(component.element);

          updatePosition(props.editor, component.element);
        }
      },
      onUpdate: (props: { editor: Editor; clientRect?: DOMRect }) => {
        component?.updateProps(props);

        if (!props.clientRect) return;
        updatePosition(props.editor, component?.element as HTMLElement);
      },
      onKeyDown: (props: { event: KeyboardEvent }) => {
        if (props.event.key === "Escape") {
          component?.destroy();
          component?.element.remove();
          return true;
        }

        const navigationKeys = ["ArrowUp", "ArrowDown", "Enter"];
        if (navigationKeys.includes(props.event.key)) {
          props.event.stopPropagation();
          if (component?.ref?.onKeyDown(props)) return true;
        }
        return false;
      },
      onExit: (props: { editor: Editor; clientRect?: DOMRect }) => {
        const utilityStorage = getExtensionStorage(
          props.editor,
          CoreExtensionNames.UTILITY
        );
        const index = utilityStorage.activeDropbarExtensions.indexOf(
          CoreExtensionNames.MENTION
        );
        if (index > -1) utilityStorage.activeDropbarExtensions.splice(index, 1);

        component?.destroy();
        component?.element.remove();
      }
    };
  };
